import { checkAndClearJustificationMarkers, notIncludes } from '@csp/csp-common-util';
import { Box, SxProps, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PaletteProps, palette } from '@mui/system';
import { Property } from 'csstype';
import { FC, JSX } from 'react';
import ReactMarkdown, { Components, ExtraProps } from 'react-markdown';
import { Theme } from '../../theme';
import { Image } from '../images/Image';
import { Body1, Body2, Body3, H1, H2, H3, H4, H5, H6 } from '../typography/TextStyles';
import { TextProps } from '../typography/model/TextProps';
import { Body } from './model/Body';
import { MarkdownColor } from './model/MarkdownColor';

const getBodyComponent = (body: Body): FC<TextProps> => {
  if (body === 'body1') {
    return Body1;
  } else if (body === 'body2') {
    return Body2;
  } else {
    return Body3;
  }
};

const markdownColor = ({ palette }: Theme, color: MarkdownColor): string => {
  const ColorsMap: Record<MarkdownColor, string> = {
    primary: palette.text.primary,
    secondary: palette.text.secondary,
    infoDark: palette.info.dark,
    warningDark: palette.warning.dark,
    errorDark: palette.error.dark,
    successDark: palette.success.dark,
    white: palette.common.white,
  };
  return ColorsMap[color];
};

const renderLi = ({ children }: JSX.IntrinsicElements['li'], body: Body): JSX.Element => {
  const Body = getBodyComponent(body);
  return (
    <li>
      <Body>{children}</Body>
    </li>
  );
};

const renderHeading = ({ children }: JSX.IntrinsicElements['h1'], Heading: FC<TextProps>): JSX.Element => (
  <Heading gutterBottom>{children}</Heading>
);

const renderBody = ({ children }: JSX.IntrinsicElements['p'], body: Body, paragraph: boolean): JSX.Element => {
  const Body = getBodyComponent(body);
  return <Body paragraph={paragraph}>{children}</Body>;
};

const renderItalicOrUnderline = (
  props: JSX.IntrinsicElements['em'],
  node: ExtraProps['node'],
  markdownSource: string,
): JSX.Element => {
  // Position of the element in the markdown source
  const start = node?.position?.start.offset;
  const end = node?.position?.end.offset;
  if (typeof start === 'number' && typeof end === 'number') {
    // Get the source markdown of the element
    const source = markdownSource.slice(start, end);
    // If the source was _text_, underline it
    if (source.startsWith('_') && source.endsWith('_')) {
      return <u {...props} />;
    }
  }

  return <em {...props} />;
};

type Props = {
  [key: string]: unknown;
  children: string;
  components?: Components;
};

export type MarkdownProps = Props & {
  color?: MarkdownColor;
  whiteSpace?: Property.WhiteSpace;
  body?: Body;
  span?: Body;
  sx?: SxProps;
  paragraph?: boolean;
  /** String put before rest of the markdown to create a header with #### for example */
  prefix?: string;
};

const WrappedLinks = styled('a')`
  overflow-wrap: break-word;
`;

type StyleProps = PaletteProps & {
  color: string;
  whiteSpace: Property.WhiteSpace;
  alignment: Property.TextAlign;
};

const MarkdownStyled = styled(Box, {
  shouldForwardProp: notIncludes(['color', 'whiteSpace', 'alignment']),
})<StyleProps>`
  ${palette}
  ${({ color }): string => `
    color: ${color};
  `}
  ${({ whiteSpace }): string => `
    white-space: ${whiteSpace};
  `}
  ${({ alignment }): string => `
    text-align: ${alignment};
  `}
`;

export const Markdown: FC<MarkdownProps> = ({
  color = 'primary',
  whiteSpace = 'normal',
  body = 'body2',
  span = 'body2',
  sx,
  paragraph = true,
  children,
  components,
  prefix = '',
}) => {
  const theme = useTheme();
  const { mdStr, alignment } = checkAndClearJustificationMarkers(children);
  const prefixedMarkdownSource = `${prefix} ${mdStr}`;

  return (
    <MarkdownStyled color={markdownColor(theme, color)} sx={sx} whiteSpace={whiteSpace} alignment={alignment}>
      <ReactMarkdown
        components={{
          h1: ({ node: _, ref: __, ...props }) => renderHeading(props, H1),
          h2: ({ node: _, ref: __, ...props }) => renderHeading(props, H2),
          h3: ({ node: _, ref: __, ...props }) => renderHeading(props, H3),
          h4: ({ node: _, ref: __, ...props }) => renderHeading(props, H4),
          h5: ({ node: _, ref: __, ...props }) => renderHeading(props, H5),
          h6: ({ node: _, ref: __, ...props }) => renderHeading(props, H6),
          li: ({ node: _, ref: __, ...props }) => renderLi(props, body),
          p: ({ node: _, ref: __, ...props }) => renderBody(props, body, paragraph),
          span: ({ node: _, ref: __, ...props }) => renderBody(props, span, paragraph),
          small: ({ node: _, ref: __, ...props }) => (
            <Body3 component="small" {...props}>
              {children}
            </Body3>
          ),
          a: ({ node: _, ref: __, ...props }) => <WrappedLinks {...props} />,
          img: ({ node: _, ref: __, ...props }) => <Image {...props} />,
          em: ({ node, ref: __, ...props }) => renderItalicOrUnderline(props, node, prefixedMarkdownSource),
          ...components,
        }}
      >
        {prefixedMarkdownSource}
      </ReactMarkdown>
    </MarkdownStyled>
  );
};
